import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import StripeCardForm from '../components/StripeCardForm';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, ShieldCheck, AlertCircle, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CardCapturePage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/card-capture/${token}`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'This link could not be loaded.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const onCardSaved = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API_URL}/api/card-capture/${token}/complete`);
      setCompleted(true);
      toast.success('Card saved — you are all set!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Card saved but we could not confirm — please contact your concierge.');
      setCompleted(true);
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-4 py-12" data-testid="card-capture-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#F56F05]/10 ring-1 ring-[#F56F05]/20 mb-3">
            <Sparkles className="h-7 w-7 text-[#F56F05]" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight">Gleam Guru</h1>
          <p className="text-sm text-zinc-400 mt-1">Save your card — securely, in under 60 seconds</p>
        </div>

        <Card className="border-0 bg-white text-zinc-900 shadow-2xl overflow-hidden">
          <CardContent className="p-6">
            {loading && (
              <div className="py-12 text-center"><Loader2 className="h-7 w-7 animate-spin text-[#F56F05] mx-auto" /></div>
            )}

            {error && !loading && (
              <div className="text-center py-8" data-testid="capture-error">
                <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
                <p className="font-bold text-zinc-900">Link unavailable</p>
                <p className="text-sm text-zinc-500 mt-1">{error}</p>
                <p className="text-xs text-zinc-400 mt-4">Reach out to your Gleam Guru concierge for a new link.</p>
              </div>
            )}

            {!loading && !error && data && !completed && (
              <>
                <p className="text-sm text-zinc-500 mb-1">Hi {data.client_name?.split(' ')[0] || 'there'},</p>
                <h2 className="text-lg font-bold mb-1">Securely save your card on file</h2>
                <p className="text-xs text-zinc-500 mb-4">
                  We only charge after each cleaning is complete — never before.
                </p>
                {data.note && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs text-orange-800 mb-4">
                    <p className="font-semibold mb-1">Message from {data.sender_name || 'your concierge'}:</p>
                    <p>{data.note}</p>
                  </div>
                )}
                <StripeCardForm
                  publishableKey={data.publishable_key}
                  clientSecret={data.client_secret}
                  cardholderName={data.client_name}
                  onSuccess={onCardSaved}
                  submitLabel="Save My Card"
                />
              </>
            )}

            {completed && (
              <div className="text-center py-8" data-testid="capture-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-3">
                  {completing ? <Loader2 className="h-8 w-8 animate-spin text-emerald-600" /> : <CheckCircle2 className="h-8 w-8 text-emerald-600" />}
                </div>
                <h3 className="text-lg font-bold text-zinc-900">You're all set, {data?.client_name?.split(' ')[0] || ''}!</h3>
                <p className="text-sm text-zinc-500 mt-2 max-w-xs mx-auto">
                  Your card is securely saved with our payment processor. {data?.sender_name || 'Your concierge'} will be in touch about scheduling your first cleaning.
                </p>
                <Link to="/" className="inline-block mt-6 text-[#F56F05] font-semibold text-sm hover:underline">
                  Visit Gleam Guru &rarr;
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-[11px] text-zinc-500 text-center mt-4 flex items-center justify-center gap-1">
          <ShieldCheck className="h-3 w-3" /> 256-bit encryption · PCI compliant · Powered by Stripe
        </p>
      </div>
    </div>
  );
};

export default CardCapturePage;
